import { normalizeTimesheetsForMission } from '../Create/draftHelpers'

export type NormalizeTimesheetsForApiMissionMeta = {
  id?: string | null
  mission_id?: string | null
  pricingType?: string | null
  missionType?: string | null
  mission_type?: string | null
  pricing_type?: string | null
}

export type NormalizeTimesheetsForApiOptions = {
  craMissionIds?: string[]
  missionIds?: string[]
  missions?: NormalizeTimesheetsForApiMissionMeta[]
}

type TimesheetLine = Record<string, unknown> & {
  mission_id?: string | null
  missionId?: string | null
  type?: string | null
  entryType?: string | null
  projectCodeId?: string | null
  project_code_id?: string | null
}

const resolveMissionBillingType = (mission: NormalizeTimesheetsForApiMissionMeta | undefined) =>
  mission?.pricingType ||
  mission?.missionType ||
  mission?.mission_type ||
  mission?.pricing_type ||
  ''

/**
 * Normalise les lignes CRA avant persistance (brouillon / soumission).
 * En multi-missions, découpe par mission_id du CRA même si une seule mission a des lignes
 * (évite d'appliquer le type de facturation de la mission primaire aux autres missions).
 */
export function normalizeTimesheetsForApi(
  timesheets: unknown,
  missionType: string | null | undefined,
  options: NormalizeTimesheetsForApiOptions = {}
): TimesheetLine[] {
  const source = Array.isArray(timesheets) ? (timesheets as TimesheetLine[]) : []
  const lineMissionIds = [
    ...new Set(
      source
        .map((line) => line?.mission_id ?? line?.missionId)
        .filter((id) => id != null && id !== '')
        .map(String)
    ),
  ]
  const craMissionIds = [
    ...new Set(
      (Array.isArray(options.craMissionIds) ? options.craMissionIds : options.missionIds || [])
        .filter((id) => id != null && id !== '')
        .map(String)
    ),
  ]
  const missions = Array.isArray(options.missions) ? options.missions : []

  const normalizeMissionSlice = (
    missionId: string,
    missionLines: TimesheetLine[],
    fallbackType: string
  ) => {
    const missionMeta = missions.find(
      (mission) => String(mission?.id ?? mission?.mission_id ?? '') === missionId
    )
    const lineType =
      missionLines.find((line) => line?.type || line?.entryType)?.type ??
      missionLines.find((line) => line?.type || line?.entryType)?.entryType ??
      (resolveMissionBillingType(missionMeta) || fallbackType || '')
    return normalizeTimesheetsForMission(missionLines, lineType) as TimesheetLine[]
  }

  const fallbackType = missionType ?? ''

  const normalized =
    craMissionIds.length > 1
      ? craMissionIds.flatMap((missionId) => {
          const missionLines = source.filter(
            (line) => String(line?.mission_id ?? line?.missionId) === missionId
          )
          return normalizeMissionSlice(missionId, missionLines, fallbackType)
        })
      : lineMissionIds.length > 1
        ? lineMissionIds.flatMap((missionId) => {
            const missionLines = source.filter(
              (line) => String(line?.mission_id ?? line?.missionId) === missionId
            )
            return normalizeMissionSlice(missionId, missionLines, fallbackType)
          })
        : (normalizeTimesheetsForMission(source, fallbackType) as TimesheetLine[])

  return normalized.map((ts) => ({
    ...ts,
    mission_id: (ts.missionId ?? ts.mission_id ?? null) as string | null,
    project_code_id: (ts.projectCodeId ?? ts.project_code_id ?? null) as string | null,
  }))
}
